import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  DetailPage,
  Field,
  Input,
  MetricStrip,
  Section,
  Select,
  Textarea,
} from "@redipos/ui";
import type { ConsoleEmailEventAuditRowApi, ConsoleEmailSuppressionApi, ConsoleEmailTemplateApi } from "@redipos/contracts";
import { plataformaApi } from "../api.js";

function errMsg(e: unknown, fallback: string) {
  return e instanceof Error ? e.message : fallback;
}

const TEMPLATE_KEYS = [
  "verify_email",
  "email_verified",
  "reset_password",
  "welcome",
  "password_changed",
  "new_login",
  "low_stock_daily_digest",
  "subscription_expiring",
  "weekly_business_digest",
];

// Coincide con EmailEvent.status en el backend (queued|sent|delivered|bounced|complained|suppressed|failed).
const EVENT_STATUSES = ["queued", "sent", "delivered", "bounced", "complained", "suppressed", "failed"];

function statusBadgeVariant(status: string): "success" | "danger" | "warning" | "muted" {
  if (status === "sent" || status === "delivered") return "success";
  if (status === "bounced" || status === "complained" || status === "failed") return "danger";
  if (status === "suppressed" || status === "queued") return "warning";
  return "muted";
}

function normalizeEnabledJson(value: unknown) {
  const source = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
  return {
    verify_email: Boolean(source.verify_email ?? true),
    email_verified: Boolean(source.email_verified ?? true),
    reset_password: Boolean(source.reset_password ?? true),
    welcome: Boolean(source.welcome ?? true),
    password_changed: Boolean(source.password_changed ?? true),
    new_login: Boolean(source.new_login ?? true),
    low_stock_daily_digest: Boolean(source.low_stock_daily_digest ?? true),
    subscription_expiring: Boolean(source.subscription_expiring ?? true),
    weekly_business_digest: Boolean(source.weekly_business_digest ?? false),
  };
}

export default function EmailSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [config, setConfig] = useState({
    mode: "disabled",
    fromName: "RediPOS",
    fromEmail: "",
    replyTo: "",
    testEmail: "",
    enabledJson: normalizeEnabledJson({}),
    resendApiKeyConfigured: false,
    resendApiKeyHint: null as string | null,
  });
  const [resendApiKeyInput, setResendApiKeyInput] = useState("");
  const [removeResendKey, setRemoveResendKey] = useState(false);
  const [templates, setTemplates] = useState<ConsoleEmailTemplateApi[]>([]);
  const [events, setEvents] = useState<ConsoleEmailEventAuditRowApi[]>([]);
  const [eventsSummary, setEventsSummary] = useState<{
    byStatus: Record<string, number>;
    byTemplate: Record<string, number>;
    retrying: number;
  }>({ byStatus: {}, byTemplate: {}, retrying: 0 });
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventFilters, setEventFilters] = useState({
    limit: 50,
    status: "",
    templateKey: "",
    toEmail: "",
  });
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("verify_email");
  const [editor, setEditor] = useState({ subject: "", html: "", text: "" });
  const [testEmail, setTestEmail] = useState("");
  const [status, setStatus] = useState("");
  const [suppressions, setSuppressions] = useState<ConsoleEmailSuppressionApi[]>([]);
  const [newSuppressionEmail, setNewSuppressionEmail] = useState("");
  const [savingSuppression, setSavingSuppression] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.key === selectedTemplateKey),
    [templates, selectedTemplateKey],
  );
  const metrics = useMemo(
    () => [
      { key: "total", label: "Total", value: eventsTotal },
      { key: "delivered", label: "Delivered", value: eventsSummary.byStatus?.delivered || 0 },
      { key: "sent", label: "Sent (sin confirmar)", value: eventsSummary.byStatus?.sent || 0 },
      { key: "bounced", label: "Bounced", value: eventsSummary.byStatus?.bounced || 0 },
      { key: "complained", label: "Complaints", value: eventsSummary.byStatus?.complained || 0 },
      { key: "suppressed", label: "Suprimidos", value: eventsSummary.byStatus?.suppressed || 0 },
      { key: "retrying", label: "Reintentos", value: eventsSummary.retrying || 0 },
      { key: "failed", label: "Failed", value: eventsSummary.byStatus?.failed || 0 },
    ],
    [eventsSummary, eventsTotal],
  );

  const load = async () => {
    setLoading(true);
    setStatus("");
    try {
      const [cfg, defaults, tpl, ev, sup] = await Promise.all([
        plataformaApi.emailConfig(),
        plataformaApi.emailEventDefaults(),
        plataformaApi.emailTemplates(),
        plataformaApi.emailEvents(eventFilters),
        plataformaApi.emailSuppressions(),
      ]);
      setConfig({
        mode: cfg.mode || "disabled",
        fromName: cfg.fromName || "RediPOS",
        fromEmail: cfg.fromEmail || "",
        replyTo: cfg.replyTo || "",
        testEmail: cfg.testEmail || "",
        enabledJson: normalizeEnabledJson(defaults?.enabledJson || cfg.enabledJson),
        resendApiKeyConfigured: Boolean(cfg.resendApiKeyConfigured),
        resendApiKeyHint: cfg.resendApiKeyHint || null,
      });
      setResendApiKeyInput("");
      setRemoveResendKey(false);
      setTemplates(Array.isArray(tpl) ? tpl : []);
      setEvents(Array.isArray(ev?.items) ? ev.items : []);
      setEventsSummary(ev?.summary || { byStatus: {}, byTemplate: {}, retrying: 0 });
      setEventsTotal(Number(ev?.total || 0));
      setSuppressions(Array.isArray(sup) ? sup : []);
    } catch (err) {
      setStatus(errMsg(err, "No se pudo cargar configuración de correo."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventFilters.limit, eventFilters.status, eventFilters.templateKey, eventFilters.toEmail]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setEditor({
      subject: selectedTemplate.subject || "",
      html: selectedTemplate.html || "",
      text: selectedTemplate.text || "",
    });
  }, [selectedTemplate]);

  const saveConfig = async () => {
    setSavingConfig(true);
    setStatus("");
    try {
      const emailBody: {
        mode: string;
        fromName: string;
        fromEmail: string;
        replyTo: string;
        testEmail: string;
        resendApiKey?: string;
      } = {
        mode: config.mode,
        fromName: config.fromName,
        fromEmail: config.fromEmail,
        replyTo: config.replyTo,
        testEmail: config.testEmail,
      };
      if (removeResendKey) {
        emailBody.resendApiKey = "";
      } else if (resendApiKeyInput.trim()) {
        emailBody.resendApiKey = resendApiKeyInput.trim();
      }
      await Promise.all([
        plataformaApi.updateEmailConfig(emailBody),
        plataformaApi.updateEmailEventDefaults(config.enabledJson),
      ]);
      setStatus("Configuracion guardada.");
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo guardar configuración."));
    } finally {
      setSavingConfig(false);
    }
  };

  const saveTemplate = async () => {
    setSavingTemplate(true);
    setStatus("");
    try {
      await plataformaApi.updateEmailTemplate(selectedTemplateKey, editor);
      setStatus(`Plantilla ${selectedTemplateKey} guardada.`);
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo guardar plantilla."));
    } finally {
      setSavingTemplate(false);
    }
  };

  const resetTemplate = async () => {
    if (!confirm(`Restablecer plantilla ${selectedTemplateKey} a valores por defecto?`)) return;
    setSavingTemplate(true);
    setStatus("");
    try {
      await plataformaApi.resetEmailTemplate(selectedTemplateKey);
      setStatus(`Plantilla ${selectedTemplateKey} restablecida.`);
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo restablecer plantilla."));
    } finally {
      setSavingTemplate(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      setStatus("Ingresa un correo para prueba.");
      return;
    }
    setSendingTest(true);
    setStatus("");
    try {
      await plataformaApi.emailTestSend(testEmail.trim().toLowerCase());
      setStatus("Correo de prueba enviado.");
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo enviar correo de prueba."));
    } finally {
      setSendingTest(false);
    }
  };

  const addSuppression = async () => {
    if (!newSuppressionEmail.trim()) return;
    setSavingSuppression(true);
    setStatus("");
    try {
      await plataformaApi.addEmailSuppression(newSuppressionEmail.trim().toLowerCase());
      setNewSuppressionEmail("");
      setStatus("Dirección agregada a la lista de supresión.");
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo agregar la supresión."));
    } finally {
      setSavingSuppression(false);
    }
  };

  const removeSuppression = async (email: string) => {
    if (!confirm(`Sacar ${email} de la lista de supresión? Va a volver a recibir correos.`)) return;
    setSavingSuppression(true);
    setStatus("");
    try {
      await plataformaApi.removeEmailSuppression(email);
      setStatus(`${email} sacado de la lista de supresión.`);
      await load();
    } catch (err) {
      setStatus(errMsg(err, "No se pudo quitar la supresión."));
    } finally {
      setSavingSuppression(false);
    }
  };

  return (
    <DetailPage
      title="Correo transaccional"
      subtitle="Configuración global de Resend, plantillas y trazabilidad de envíos."
      shellSpacing="lg"
    >
      {status ? (
        <div className="rounded-input border border-border bg-surface-elevated px-3 py-2 text-sm text-fg-muted">
          {status}
        </div>
      ) : null}

      <Section
        title="Configuración"
        actions={
          <Badge variant={config.mode === "live" ? "success" : config.mode === "test" ? "warning" : "muted"}>
            {config.mode.toUpperCase()}
          </Badge>
        }
      >
        <p className="text-xs text-fg-subtle mb-3">
          La clave de Resend se persiste en la base de datos (la API no devuelve el valor, solo un indicio si está
          guardada). Sustituye a definir <code className="text-xs">RESEND_API_KEY</code> en el entorno.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2 space-y-2">
            <Field
              label="API key de Resend"
              hint={
                config.resendApiKeyConfigured
                  ? `Clave actual: ${config.resendApiKeyHint || "guardada"}. Escribí una nueva para reemplazarla.`
                  : "Sin clave: los envíos fallarán hasta que guardes una key válida."
              }
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={resendApiKeyInput}
                onChange={(e) => setResendApiKeyInput(e.target.value)}
                disabled={loading || removeResendKey}
                placeholder="re_… (dejar vacío para no cambiar)"
                className="font-mono text-sm"
              />
            </Field>
            <label className="flex items-center gap-2 text-sm text-fg-muted">
              <Checkbox
                checked={removeResendKey}
                onChange={(e) => setRemoveResendKey(e.target.checked)}
                disabled={loading || !config.resendApiKeyConfigured}
              />
              Quitar clave guardada
            </label>
          </div>
          <Field label="Modo">
            <Select
              value={config.mode}
              onChange={(e) => setConfig((prev) => ({ ...prev, mode: e.target.value }))}
              disabled={loading}
            >
              <option value="disabled">disabled</option>
              <option value="test">test</option>
              <option value="live">live</option>
            </Select>
          </Field>
          <Field label="From name">
            <Input
              value={config.fromName}
              onChange={(e) => setConfig((prev) => ({ ...prev, fromName: e.target.value }))}
              disabled={loading}
            />
          </Field>
          <Field label="From email">
            <Input
              value={config.fromEmail}
              onChange={(e) => setConfig((prev) => ({ ...prev, fromEmail: e.target.value }))}
              disabled={loading}
            />
          </Field>
          <Field label="Reply-To (opcional)">
            <Input
              value={config.replyTo}
              onChange={(e) => setConfig((prev) => ({ ...prev, replyTo: e.target.value }))}
              disabled={loading}
            />
          </Field>
          <Field label="Email de testing (modo test)">
            <Input
              value={config.testEmail}
              onChange={(e) => setConfig((prev) => ({ ...prev, testEmail: e.target.value }))}
              disabled={loading}
            />
          </Field>
        </div>
        <div className="flex flex-wrap gap-4">
          {TEMPLATE_KEYS.map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm text-fg-muted">
              <Checkbox
                checked={Boolean((config.enabledJson as Record<string, boolean>)[key])}
                onChange={(e) =>
                  setConfig((prev) => ({
                    ...prev,
                    enabledJson: {
                      ...prev.enabledJson,
                      [key]: e.target.checked,
                    },
                  }))
                }
              />
              {key}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={saveConfig} loading={savingConfig}>
            Guardar configuración
          </Button>
          <Input
            placeholder="correo@ejemplo.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="max-w-xs"
          />
          <Button type="button" variant="secondary" onClick={sendTest} loading={sendingTest}>
            Enviar prueba
          </Button>
        </div>
      </Section>

      <Section title="Plantillas">
        <Field label="Plantilla">
          <Select value={selectedTemplateKey} onChange={(e) => setSelectedTemplateKey(e.target.value)} disabled={loading}>
            {TEMPLATE_KEYS.map((key) => (
              <option key={key} value={key}>
                {key}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Subject">
          <Input value={editor.subject} onChange={(e) => setEditor((prev) => ({ ...prev, subject: e.target.value }))} />
        </Field>
        <Field label="HTML">
          <Textarea
            rows={8}
            value={editor.html}
            onChange={(e) => setEditor((prev) => ({ ...prev, html: e.target.value }))}
          />
        </Field>
        <Field label="Texto plano">
          <Textarea
            rows={5}
            value={editor.text}
            onChange={(e) => setEditor((prev) => ({ ...prev, text: e.target.value }))}
          />
        </Field>
        <div className="flex items-center gap-2">
          <Button type="button" onClick={saveTemplate} loading={savingTemplate}>
            Guardar plantilla
          </Button>
          <Button type="button" variant="secondary" onClick={resetTemplate} loading={savingTemplate}>
            Restablecer por defecto
          </Button>
        </div>
      </Section>

      <Section title="Eventos recientes" subtitle="Auditoría operativa con filtros y seguimiento de reintentos.">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Field label="Estado">
            <Select
              value={eventFilters.status}
              onChange={(e) => setEventFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Todos</option>
              {EVENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Template">
            <Select
              value={eventFilters.templateKey}
              onChange={(e) => setEventFilters((prev) => ({ ...prev, templateKey: e.target.value }))}
            >
              <option value="">Todos</option>
              {TEMPLATE_KEYS.map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Destino contiene">
            <Input
              value={eventFilters.toEmail}
              onChange={(e) => setEventFilters((prev) => ({ ...prev, toEmail: e.target.value }))}
              placeholder="correo@..."
            />
          </Field>
          <Field label="Límite">
            <Select
              value={String(eventFilters.limit)}
              onChange={(e) => setEventFilters((prev) => ({ ...prev, limit: Number(e.target.value) || 50 }))}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </Field>
        </div>
        <MetricStrip items={metrics} />
        <div className="table-x-scroll">
          <table className="w-full text-sm">
            <thead className="text-left text-fg-subtle">
              <tr>
                <th className="py-2">Fecha</th>
                <th className="py-2">Template</th>
                <th className="py-2">Destino</th>
                <th className="py-2">Estado</th>
                <th className="py-2">Intentos</th>
                <th className="py-2">Próximo intento</th>
                <th className="py-2">Error</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-3 text-fg-muted">
                    Sin eventos por ahora.
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr key={event.id} className="border-t border-border/70">
                    <td className="py-2 text-fg-muted">{new Date(event.createdAt).toLocaleString()}</td>
                    <td className="py-2">{event.templateKey}</td>
                    <td className="py-2">{event.toEmail}</td>
                    <td className="py-2">
                      <Badge variant={statusBadgeVariant(event.status)}>{event.status}</Badge>
                    </td>
                    <td className="py-2 text-fg-muted">{event.attempts ?? 0}</td>
                    <td className="py-2 text-fg-muted">
                      {event.nextAttemptAt ? new Date(event.nextAttemptAt).toLocaleString() : "-"}
                    </td>
                    <td className="py-2 text-fg-muted">{event.errorMessage || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Supresiones"
        subtitle="Direcciones a las que ya no se les manda correo — bounce/complaint automático (vía webhook de Resend) o carga manual."
      >
        <div className="flex items-center gap-2">
          <Input
            placeholder="correo@ejemplo.com"
            value={newSuppressionEmail}
            onChange={(e) => setNewSuppressionEmail(e.target.value)}
            className="max-w-xs"
          />
          <Button type="button" variant="secondary" onClick={addSuppression} loading={savingSuppression}>
            Agregar a mano
          </Button>
        </div>
        <div className="table-x-scroll">
          <table className="w-full text-sm">
            <thead className="text-left text-fg-subtle">
              <tr>
                <th className="py-2">Email</th>
                <th className="py-2">Motivo</th>
                <th className="py-2">Detalle</th>
                <th className="py-2">Desde</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {suppressions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-3 text-fg-muted">
                    Sin direcciones suprimidas.
                  </td>
                </tr>
              ) : (
                suppressions.map((row) => (
                  <tr key={row.id} className="border-t border-border/70">
                    <td className="py-2">{row.email}</td>
                    <td className="py-2">
                      <Badge variant={row.reason === "manual" ? "muted" : "danger"}>{row.reason}</Badge>
                    </td>
                    <td className="py-2 text-fg-muted">{row.detail || "-"}</td>
                    <td className="py-2 text-fg-muted">{new Date(row.createdAt).toLocaleString()}</td>
                    <td className="py-2 text-right">
                      <Button type="button" variant="secondary" onClick={() => removeSuppression(row.email)}>
                        Quitar
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </DetailPage>
  );
}
